import { useState, useEffect } from "react";
import axios from "axios";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import {
  Briefcase,
  CalendarDays,
  Clock,
  User,
  Phone,
  Mail,
  FileText,
  MoreVertical,
  Plus,
  Trash2,
  Banknote,
  Users,
  Shield,
  Pencil,
  CheckCircle2,
  XCircle,
  X,
} from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ROLE_ICONS = [
  { value: "stethoscope", label: "Doctor" },
  { value: "users", label: "Nurse" },
  { value: "flask-conical", label: "Lab" },
  { value: "clipboard-list", label: "Reception" },
  { value: "briefcase", label: "Admin" },
  { value: "calculator", label: "Finance" },
];

const JOB_TYPES = ["Full-Time", "Part-Time", "Part-Time / Locum", "Contract", "Internship"];

export default function ManagerDashboard() {
  const [isAuthed, setIsAuthed] = useState(false);
  const [pin, setPin] = useState("");

  const [jobs, setJobs] = useState([]);
  const [shifts, setShifts] = useState([]);
  const [careerApps, setCareerApps] = useState([]);
  const [shiftApps, setShiftApps] = useState([]);

  const [showJobForm, setShowJobForm] = useState(false);
  const [editingJob, setEditingJob] = useState(null);
  const [jobForm, setJobForm] = useState({
    title: "", type: "Full-Time", icon: "briefcase", description: "", requirements: [""],
  });

  const [showShiftForm, setShowShiftForm] = useState(false);
  const [shiftForm, setShiftForm] = useState({
    title: "", role: "Locum Doctor", date: "", start_time: "", end_time: "",
    rate: "", description: "", slots_available: 1,
  });

  const fetchData = async () => {
    try {
      const [jobsRes, shiftsRes, careerRes, shiftAppRes] = await Promise.all([
        axios.get(`${API}/jobs`),
        axios.get(`${API}/shifts`),
        axios.get(`${API}/career-applications`),
        axios.get(`${API}/shift-applications`),
      ]);
      setJobs(jobsRes.data);
      setShifts(shiftsRes.data);
      setCareerApps(careerRes.data);
      setShiftApps(shiftAppRes.data);
    } catch {
      toast.error("Failed to load data");
    }
  };

  useEffect(() => {
    if (isAuthed) {
      // Seed jobs on first load
      axios.post(`${API}/seed-jobs`).catch(() => {});
      fetchData();
    }
  }, [isAuthed]);

  // ─── Job CRUD ───
  const resetJobForm = () => {
    setJobForm({ title: "", type: "Full-Time", icon: "briefcase", description: "", requirements: [""] });
    setEditingJob(null);
    setShowJobForm(false);
  };

  const handleSaveJob = async () => {
    if (!jobForm.title || !jobForm.description) {
      toast.error("Please fill in job title and description");
      return;
    }
    const payload = {
      ...jobForm,
      requirements: jobForm.requirements.filter((r) => r.trim() !== ""),
    };
    try {
      if (editingJob) {
        await axios.put(`${API}/jobs/${editingJob}`, payload);
        toast.success("Job updated");
      } else {
        await axios.post(`${API}/jobs`, payload);
        toast.success("Job created");
      }
      resetJobForm();
      fetchData();
    } catch {
      toast.error("Failed to save job");
    }
  };

  const handleEditJob = (job) => {
    setJobForm({
      title: job.title,
      type: job.type,
      icon: job.icon,
      description: job.description,
      requirements: job.requirements.length > 0 ? [...job.requirements] : [""],
    });
    setEditingJob(job.id);
    setShowJobForm(true);
  };

  const handleDeleteJob = async (id) => {
    try {
      await axios.delete(`${API}/jobs/${id}`);
      toast.success("Job deleted");
      fetchData();
    } catch {
      toast.error("Failed to delete job");
    }
  };

  const toggleJobActive = async (job) => {
    try {
      await axios.put(`${API}/jobs/${job.id}`, { is_active: !job.is_active });
      toast.success(job.is_active ? "Job deactivated" : "Job activated");
      fetchData();
    } catch {
      toast.error("Failed to update job");
    }
  };

  // ─── Shift CRUD ───
  const createShift = async () => {
    if (!shiftForm.title || !shiftForm.date || !shiftForm.start_time || !shiftForm.end_time) {
      toast.error("Please fill in shift title, date, and times");
      return;
    }
    try {
      await axios.post(`${API}/shifts`, shiftForm);
      toast.success("Shift created");
      setShowShiftForm(false);
      setShiftForm({ title: "", role: "Locum Doctor", date: "", start_time: "", end_time: "", rate: "", description: "", slots_available: 1 });
      fetchData();
    } catch {
      toast.error("Failed to create shift");
    }
  };

  const deleteShift = async (id) => {
    try {
      await axios.delete(`${API}/shifts/${id}`);
      toast.success("Shift deleted");
      fetchData();
    } catch {
      toast.error("Failed to delete shift");
    }
  };

  const toggleShiftStatus = async (shift) => {
    try {
      if (shift.status === "open") {
        await axios.put(`${API}/shifts/${shift.id}/close`);
        toast.success("Shift closed");
      } else {
        await axios.put(`${API}/shifts/${shift.id}/reopen`);
        toast.success("Shift reopened");
      }
      fetchData();
    } catch {
      toast.error("Failed to update shift");
    }
  };

  // ─── Application Status ───
  const updateCareerAppStatus = async (id, status) => {
    try {
      await axios.put(`${API}/career-applications/${id}/status`, { status });
      toast.success(`Application ${status}`);
      fetchData();
    } catch {
      toast.error("Failed to update");
    }
  };

  const updateShiftAppStatus = async (id, status) => {
    try {
      await axios.put(`${API}/shift-applications/${id}/status`, { status });
      toast.success(`Application ${status}`);
      fetchData();
    } catch {
      toast.error("Failed to update");
    }
  };

  // ─── Requirement helpers ───
  const addRequirement = () => setJobForm({ ...jobForm, requirements: [...jobForm.requirements, ""] });
  const updateRequirement = (idx, val) => {
    const reqs = [...jobForm.requirements];
    reqs[idx] = val;
    setJobForm({ ...jobForm, requirements: reqs });
  };
  const removeRequirement = (idx) => {
    const reqs = jobForm.requirements.filter((_, i) => i !== idx);
    setJobForm({ ...jobForm, requirements: reqs.length > 0 ? reqs : [""] });
  };

  // ─── Login Screen ───
  if (!isAuthed) {
    return (
      <div className="min-h-screen bg-stone-50 flex items-center justify-center px-4 pt-20">
        <div className="max-w-sm w-full bg-white rounded-3xl p-8 border border-stone-100 shadow-sm text-center" data-testid="manager-login">
          <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <Briefcase className="w-8 h-8 text-blue-700" />
          </div>
          <h2 className="font-[Outfit] text-xl font-bold text-stone-900 mb-2">Clinic Manager</h2>
          <p className="text-stone-500 text-sm mb-6">Enter the manager password to continue</p>
          <input
            data-testid="manager-pin-input"
            type="password"
            placeholder="Enter Password"
            value={pin}
            onChange={(e) => setPin(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                if (pin === "Talo12qaz") setIsAuthed(true);
                else toast.error("Invalid password");
              }
            }}
            className="w-full px-4 py-3 rounded-xl border border-stone-200 bg-stone-50 text-center text-lg tracking-widest mb-4 focus:outline-none focus:ring-2 focus:ring-blue-600"
          />
          <Button
            data-testid="manager-login-btn"
            onClick={() => {
              if (pin === "Talo12qaz") setIsAuthed(true);
              else toast.error("Invalid password");
            }}
            className="w-full bg-blue-700 hover:bg-blue-800 text-white rounded-full py-3"
          >
            Enter Manager Portal
          </Button>
          <p className="text-xs text-stone-400 mt-4">Contact clinic admin for access</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-stone-50 pt-24 pb-16 px-4" data-testid="manager-dashboard">
      <div className="max-w-6xl mx-auto">
        <div className="mb-8">
          <h1 className="font-[Outfit] text-3xl font-bold text-stone-900 tracking-tight">
            Clinic Manager Portal
          </h1>
          <p className="text-stone-500 mt-1">Manage job listings, shifts, and applications</p>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          {[
            { label: "Active Jobs", value: jobs.filter((j) => j.is_active).length, color: "bg-blue-100 text-blue-800" },
            { label: "Open Shifts", value: shifts.filter((s) => s.status === "open").length, color: "bg-orange-100 text-orange-800" },
            { label: "Job Applications", value: careerApps.length, color: "bg-green-100 text-green-800" },
            { label: "Shift Applications", value: shiftApps.length, color: "bg-cyan-100 text-cyan-800" },
          ].map((stat) => (
            <div key={stat.label} className="bg-white rounded-2xl border border-stone-100 p-5">
              <p className="text-stone-500 text-xs font-medium">{stat.label}</p>
              <p className="text-2xl font-bold text-stone-900 mt-1">{stat.value}</p>
            </div>
          ))}
        </div>

        <Tabs defaultValue="jobs" className="w-full">
          <TabsList className="bg-white border border-stone-100 rounded-xl p-1 mb-6 flex-wrap">
            <TabsTrigger value="jobs" className="rounded-lg data-[state=active]:bg-blue-700 data-[state=active]:text-white" data-testid="tab-jobs">
              <Briefcase className="w-4 h-4 mr-2" /> Jobs
            </TabsTrigger>
            <TabsTrigger value="shifts" className="rounded-lg data-[state=active]:bg-blue-700 data-[state=active]:text-white" data-testid="tab-manager-shifts">
              <CalendarDays className="w-4 h-4 mr-2" /> Shifts
            </TabsTrigger>
            <TabsTrigger value="job-applications" className="rounded-lg data-[state=active]:bg-blue-700 data-[state=active]:text-white" data-testid="tab-job-apps">
              <FileText className="w-4 h-4 mr-2" /> Job Apps ({careerApps.length})
            </TabsTrigger>
            <TabsTrigger value="shift-applications" className="rounded-lg data-[state=active]:bg-blue-700 data-[state=active]:text-white" data-testid="tab-shift-apps">
              <Users className="w-4 h-4 mr-2" /> Shift Apps ({shiftApps.length})
            </TabsTrigger>
          </TabsList>

          {/* ═══ JOBS TAB ═══ */}
          <TabsContent value="jobs">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-[Outfit] text-lg font-semibold text-stone-900">Job Listings</h3>
              <Button data-testid="add-job-btn" onClick={() => { resetJobForm(); setShowJobForm(true); }} className="bg-blue-700 hover:bg-blue-800 text-white rounded-full px-5 gap-2">
                <Plus className="w-4 h-4" /> Add Job
              </Button>
            </div>

            {showJobForm && (
              <div className="bg-stone-50 rounded-2xl border border-stone-200 p-6 mb-6" data-testid="job-form">
                <h4 className="font-semibold text-stone-900 mb-4">{editingJob ? "Edit Job" : "New Job Listing"}</h4>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-4">
                  <div>
                    <Label className="text-sm text-stone-600 mb-1 block">Job Title *</Label>
                    <Input data-testid="job-title-input" placeholder="e.g. Locum Doctor" value={jobForm.title} onChange={(e) => setJobForm({ ...jobForm, title: e.target.value })} className="bg-white rounded-xl border-stone-200" />
                  </div>
                  <div>
                    <Label className="text-sm text-stone-600 mb-1 block">Job Type</Label>
                    <Select value={jobForm.type} onValueChange={(v) => setJobForm({ ...jobForm, type: v })}>
                      <SelectTrigger className="bg-white rounded-xl border-stone-200" data-testid="job-type-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {JOB_TYPES.map((t) => (
                          <SelectItem key={t} value={t}>{t}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-sm text-stone-600 mb-1 block">Icon</Label>
                    <Select value={jobForm.icon} onValueChange={(v) => setJobForm({ ...jobForm, icon: v })}>
                      <SelectTrigger className="bg-white rounded-xl border-stone-200" data-testid="job-icon-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {ROLE_ICONS.map((ic) => (
                          <SelectItem key={ic.value} value={ic.value}>{ic.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="mb-4">
                  <Label className="text-sm text-stone-600 mb-1 block">Description *</Label>
                  <Textarea data-testid="job-desc-input" placeholder="Describe the role and responsibilities..." value={jobForm.description} onChange={(e) => setJobForm({ ...jobForm, description: e.target.value })} className="bg-white rounded-xl border-stone-200 min-h-[80px]" />
                </div>
                <div className="mb-4">
                  <Label className="text-sm text-stone-600 mb-2 block">Requirements</Label>
                  {jobForm.requirements.map((req, idx) => (
                    <div key={idx} className="flex items-center gap-2 mb-2">
                      <Input
                        data-testid={`job-req-input-${idx}`}
                        placeholder="e.g. MBBS or equivalent qualification"
                        value={req}
                        onChange={(e) => updateRequirement(idx, e.target.value)}
                        className="bg-white rounded-xl border-stone-200 flex-1"
                      />
                      <Button type="button" variant="ghost" size="sm" onClick={() => removeRequirement(idx)} className="text-stone-400 hover:text-red-500 p-1">
                        <X className="w-4 h-4" />
                      </Button>
                    </div>
                  ))}
                  <Button type="button" variant="outline" size="sm" onClick={addRequirement} className="rounded-full text-xs mt-1 gap-1">
                    <Plus className="w-3 h-3" /> Add Requirement
                  </Button>
                </div>
                <div className="flex gap-3">
                  <Button data-testid="save-job-btn" onClick={handleSaveJob} className="bg-blue-700 hover:bg-blue-800 text-white rounded-full px-6">
                    {editingJob ? "Update Job" : "Save Job"}
                  </Button>
                  <Button variant="outline" onClick={resetJobForm} className="rounded-full px-6 border-stone-200">Cancel</Button>
                </div>
              </div>
            )}

            {jobs.length === 0 && (
              <div className="bg-white rounded-2xl border border-stone-100 p-12 text-center">
                <Briefcase className="w-12 h-12 text-stone-300 mx-auto mb-3" />
                <p className="text-stone-400">No job listings yet. Click "Add Job" to create one.</p>
              </div>
            )}

            <div className="space-y-3">
              {jobs.map((job) => (
                <div key={job.id} className="bg-white rounded-2xl border border-stone-100 p-5" data-testid={`manager-job-${job.id}`}>
                  <div className="flex flex-col sm:flex-row sm:items-center gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-3 mb-1 flex-wrap">
                        <p className="font-semibold text-stone-900">{job.title}</p>
                        <Badge className="bg-blue-100 text-blue-800 border-0 rounded-full text-xs">{job.type}</Badge>
                        <Badge className={`text-xs rounded-full border-0 ${job.is_active ? "bg-green-100 text-green-800" : "bg-stone-200 text-stone-500"}`}>
                          {job.is_active ? "Active" : "Inactive"}
                        </Badge>
                      </div>
                      <p className="text-sm text-stone-600 mb-2 line-clamp-2">{job.description}</p>
                      <div className="flex flex-wrap gap-1">
                        {job.requirements?.slice(0, 3).map((r, i) => (
                          <span key={i} className="text-xs bg-stone-50 text-stone-500 rounded-full px-2 py-0.5">{r}</span>
                        ))}
                        {job.requirements?.length > 3 && (
                          <span className="text-xs text-stone-400">+{job.requirements.length - 3} more</span>
                        )}
                      </div>
                      <p className="text-xs text-stone-400 mt-2">
                        Applications: {careerApps.filter((a) => a.job_id === job.id).length}
                      </p>
                    </div>
                    <div className="flex gap-2 flex-shrink-0">
                      <Button size="sm" variant="outline" onClick={() => handleEditJob(job)} className="rounded-xl text-xs gap-1" data-testid={`edit-job-${job.id}`}>
                        <Pencil className="w-3 h-3" /> Edit
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => toggleJobActive(job)} className="rounded-xl text-xs" data-testid={`toggle-job-${job.id}`}>
                        {job.is_active ? "Deactivate" : "Activate"}
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => handleDeleteJob(job.id)} className="rounded-xl text-xs border-red-200 text-red-600 hover:bg-red-50" data-testid={`delete-job-${job.id}`}>
                        <Trash2 className="w-3 h-3" />
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </TabsContent>

          {/* ═══ SHIFTS TAB ═══ */}
          <TabsContent value="shifts">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-[Outfit] text-lg font-semibold text-stone-900">Manage Shifts</h3>
              <Button data-testid="manager-add-shift-btn" onClick={() => setShowShiftForm(!showShiftForm)} className="bg-blue-700 hover:bg-blue-800 text-white rounded-full px-5 gap-2">
                <Plus className="w-4 h-4" /> Add Shift
              </Button>
            </div>

            {showShiftForm && (
              <div className="bg-stone-50 rounded-2xl border border-stone-200 p-6 mb-6" data-testid="manager-shift-form">
                <h4 className="font-semibold text-stone-900 mb-4">New Shift</h4>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-4">
                  <div>
                    <Label className="text-sm text-stone-600 mb-1 block">Shift Title *</Label>
                    <Input data-testid="manager-shift-title" placeholder="e.g. Morning Ward Cover" value={shiftForm.title} onChange={(e) => setShiftForm({ ...shiftForm, title: e.target.value })} className="bg-white rounded-xl border-stone-200" />
                  </div>
                  <div>
                    <Label className="text-sm text-stone-600 mb-1 block">Role *</Label>
                    <Select value={shiftForm.role} onValueChange={(v) => setShiftForm({ ...shiftForm, role: v })}>
                      <SelectTrigger className="bg-white rounded-xl border-stone-200" data-testid="manager-shift-role">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {["Locum Doctor", "Nurse", "Lab Scientist", "Lab Technician", "Receptionist", "Admin Staff"].map((r) => (
                          <SelectItem key={r} value={r}>{r}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-sm text-stone-600 mb-1 block">Date *</Label>
                    <Input data-testid="manager-shift-date" type="date" value={shiftForm.date} onChange={(e) => setShiftForm({ ...shiftForm, date: e.target.value })} className="bg-white rounded-xl border-stone-200" />
                  </div>
                  <div>
                    <Label className="text-sm text-stone-600 mb-1 block">Start Time *</Label>
                    <Input data-testid="manager-shift-start" type="time" value={shiftForm.start_time} onChange={(e) => setShiftForm({ ...shiftForm, start_time: e.target.value })} className="bg-white rounded-xl border-stone-200" />
                  </div>
                  <div>
                    <Label className="text-sm text-stone-600 mb-1 block">End Time *</Label>
                    <Input data-testid="manager-shift-end" type="time" value={shiftForm.end_time} onChange={(e) => setShiftForm({ ...shiftForm, end_time: e.target.value })} className="bg-white rounded-xl border-stone-200" />
                  </div>
                  <div>
                    <Label className="text-sm text-stone-600 mb-1 block">Rate (Optional)</Label>
                    <Input data-testid="manager-shift-rate" placeholder="e.g. ₦25,000/shift" value={shiftForm.rate} onChange={(e) => setShiftForm({ ...shiftForm, rate: e.target.value })} className="bg-white rounded-xl border-stone-200" />
                  </div>
                  <div>
                    <Label className="text-sm text-stone-600 mb-1 block">Slots Available</Label>
                    <Input data-testid="manager-shift-slots" type="number" min="1" value={shiftForm.slots_available} onChange={(e) => setShiftForm({ ...shiftForm, slots_available: parseInt(e.target.value) || 1 })} className="bg-white rounded-xl border-stone-200" />
                  </div>
                  <div className="sm:col-span-2">
                    <Label className="text-sm text-stone-600 mb-1 block">Description (Optional)</Label>
                    <Input data-testid="manager-shift-desc" placeholder="Brief description of shift duties..." value={shiftForm.description} onChange={(e) => setShiftForm({ ...shiftForm, description: e.target.value })} className="bg-white rounded-xl border-stone-200" />
                  </div>
                </div>
                <div className="flex gap-3">
                  <Button data-testid="manager-save-shift-btn" onClick={createShift} className="bg-blue-700 hover:bg-blue-800 text-white rounded-full px-6">Save Shift</Button>
                  <Button variant="outline" onClick={() => setShowShiftForm(false)} className="rounded-full px-6 border-stone-200">Cancel</Button>
                </div>
              </div>
            )}

            {shifts.length === 0 && (
              <div className="bg-white rounded-2xl border border-stone-100 p-12 text-center">
                <CalendarDays className="w-12 h-12 text-stone-300 mx-auto mb-3" />
                <p className="text-stone-400">No shifts created yet.</p>
              </div>
            )}

            <div className="space-y-3">
              {shifts.map((shift) => (
                <div key={shift.id} className="bg-white rounded-2xl border border-stone-100 p-5" data-testid={`manager-shift-${shift.id}`}>
                  <div className="flex flex-col sm:flex-row sm:items-center gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-3 mb-2 flex-wrap">
                        <p className="font-semibold text-stone-900">{shift.role}</p>
                        <Badge className={`text-xs rounded-full border-0 ${shift.status === "open" ? "bg-green-100 text-green-800" : "bg-stone-200 text-stone-600"}`}>
                          {shift.status}
                        </Badge>
                        <Badge className="text-xs rounded-full border-0 bg-blue-100 text-blue-800">
                          {shift.slots_filled}/{shift.slots_available} filled
                        </Badge>
                      </div>
                      <p className="text-sm text-stone-700 font-medium mb-1">{shift.title}</p>
                      <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-stone-500">
                        <span className="flex items-center gap-1"><CalendarDays className="w-3 h-3" />{shift.date}</span>
                        <span className="flex items-center gap-1"><Clock className="w-3 h-3" />{shift.start_time} - {shift.end_time}</span>
                        {shift.rate && <span className="flex items-center gap-1"><Banknote className="w-3 h-3" />{shift.rate}</span>}
                      </div>
                      <p className="text-xs text-stone-400 mt-1">
                        Applications: {shiftApps.filter((a) => a.shift_id === shift.id).length}
                      </p>
                    </div>
                    <div className="flex gap-2 flex-shrink-0">
                      <Button size="sm" variant="outline" onClick={() => toggleShiftStatus(shift)} className="rounded-xl text-xs" data-testid={`manager-toggle-shift-${shift.id}`}>
                        {shift.status === "open" ? "Close" : "Reopen"}
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => deleteShift(shift.id)} className="rounded-xl text-xs border-red-200 text-red-600 hover:bg-red-50" data-testid={`manager-delete-shift-${shift.id}`}>
                        <Trash2 className="w-3 h-3" />
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </TabsContent>

          {/* ═══ JOB APPLICATIONS TAB ═══ */}
          <TabsContent value="job-applications">
            <h3 className="font-[Outfit] text-lg font-semibold text-stone-900 mb-4">Job Applications</h3>
            {careerApps.length === 0 && (
              <div className="bg-white rounded-2xl border border-stone-100 p-12 text-center">
                <FileText className="w-12 h-12 text-stone-300 mx-auto mb-3" />
                <p className="text-stone-400">No job applications yet</p>
              </div>
            )}
            <div className="space-y-3">
              {careerApps.map((app) => (
                <div key={app.id} className="bg-white rounded-2xl border border-stone-100 p-5" data-testid={`career-app-${app.id}`}>
                  <div className="flex flex-col sm:flex-row sm:items-start gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-3 mb-2 flex-wrap">
                        <p className="font-semibold text-stone-900">{app.applicant_name}</p>
                        <Badge className="bg-blue-100 text-blue-800 border-0 rounded-full text-xs">{app.job_title}</Badge>
                        <Badge className={`text-xs rounded-full border-0 ${app.status === "pending" ? "bg-amber-100 text-amber-800" : app.status === "accepted" ? "bg-green-100 text-green-800" : "bg-red-100 text-red-800"}`}>
                          {app.status}
                        </Badge>
                      </div>
                      <div className="flex flex-wrap gap-3 text-xs text-stone-500 mb-2">
                        <span className="flex items-center gap-1"><Mail className="w-3 h-3" />{app.email}</span>
                        <span className="flex items-center gap-1"><Phone className="w-3 h-3" />{app.phone}</span>
                        {app.registration && <span className="flex items-center gap-1"><FileText className="w-3 h-3" />{app.registration}</span>}
                        {app.cv_url && (
                          <a href={`${API.replace('/api', '')}${app.cv_url}`} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 text-blue-700 underline">
                            <FileText className="w-3 h-3" />View CV
                          </a>
                        )}
                      </div>
                      {app.experience && <p className="text-xs text-stone-600 mb-1"><strong>Experience:</strong> {app.experience}</p>}
                      {app.message && <p className="text-xs text-stone-500 italic">"{app.message}"</p>}
                      <p className="text-xs text-stone-400 mt-1">{new Date(app.created_at).toLocaleDateString()}</p>
                    </div>
                    {app.status === "pending" && (
                      <div className="flex gap-2 flex-shrink-0">
                        <Button size="sm" onClick={() => updateCareerAppStatus(app.id, "accepted")} className="bg-green-700 hover:bg-green-800 text-white rounded-xl text-xs" data-testid={`accept-career-app-${app.id}`}>
                          Accept
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => updateCareerAppStatus(app.id, "rejected")} className="rounded-xl text-xs border-red-200 text-red-600 hover:bg-red-50" data-testid={`reject-career-app-${app.id}`}>
                          Reject
                        </Button>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </TabsContent>

          {/* ═══ SHIFT APPLICATIONS TAB ═══ */}
          <TabsContent value="shift-applications">
            <h3 className="font-[Outfit] text-lg font-semibold text-stone-900 mb-4">Shift Applications</h3>
            {shiftApps.length === 0 && (
              <div className="bg-white rounded-2xl border border-stone-100 p-12 text-center">
                <Users className="w-12 h-12 text-stone-300 mx-auto mb-3" />
                <p className="text-stone-400">No shift applications yet</p>
              </div>
            )}
            <div className="space-y-3">
              {shiftApps.map((app) => (
                <div key={app.id} className="bg-white rounded-2xl border border-stone-100 p-5" data-testid={`shift-app-${app.id}`}>
                  <div className="flex flex-col sm:flex-row sm:items-start gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-3 mb-2 flex-wrap">
                        <p className="font-semibold text-stone-900">{app.applicant_name}</p>
                        <Badge className="bg-orange-100 text-orange-800 border-0 rounded-full text-xs">{app.shift_title}</Badge>
                        <Badge className={`text-xs rounded-full border-0 ${app.status === "pending" ? "bg-amber-100 text-amber-800" : app.status === "accepted" ? "bg-green-100 text-green-800" : "bg-red-100 text-red-800"}`}>
                          {app.status}
                        </Badge>
                      </div>
                      <div className="flex flex-wrap gap-3 text-xs text-stone-500 mb-2">
                        <span className="flex items-center gap-1"><Mail className="w-3 h-3" />{app.email}</span>
                        <span className="flex items-center gap-1"><Phone className="w-3 h-3" />{app.phone}</span>
                        <span className="flex items-center gap-1"><CalendarDays className="w-3 h-3" />{app.shift_date}</span>
                        {app.registration && <span className="flex items-center gap-1"><FileText className="w-3 h-3" />{app.registration}</span>}
                        {app.cv_url && (
                          <a href={`${API.replace('/api', '')}${app.cv_url}`} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 text-blue-700 underline">
                            <FileText className="w-3 h-3" />View CV
                          </a>
                        )}
                      </div>
                      {app.experience && <p className="text-xs text-stone-600 mb-1"><strong>Experience:</strong> {app.experience}</p>}
                      {app.message && <p className="text-xs text-stone-500 italic">"{app.message}"</p>}
                    </div>
                    {app.status === "pending" && (
                      <div className="flex gap-2 flex-shrink-0">
                        <Button size="sm" onClick={() => updateShiftAppStatus(app.id, "accepted")} className="bg-green-700 hover:bg-green-800 text-white rounded-xl text-xs" data-testid={`accept-shift-app-${app.id}`}>
                          Accept
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => updateShiftAppStatus(app.id, "rejected")} className="rounded-xl text-xs border-red-200 text-red-600 hover:bg-red-50" data-testid={`reject-shift-app-${app.id}`}>
                          Reject
                        </Button>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
